<template>
	<div class="d-flex flex-column h-100">
		<div class="d-flex h-100 flex-fill justify-content-center align-items-center" v-if="loading>0">
			<b-icon icon="circle-fill" animation="throb" font-scale="2"></b-icon>
		</div>
		<draggable v-else-if="channels?.length > 0" v-model="channels" tag="ul" class="channel-list" handle=".channel-drag-handle" :animation="150" ghost-class="channel-ghost" @end="persistOrder">
			<li v-for="channel in channels" :key="`${channel.service_id}-${channel.token}`">
				<div class="channel-drag-handle" title="Drag to reorder"><b-icon icon="grip-vertical"></b-icon></div>
				<div class="channel-rows">
					<div class="content">
						<div class="channel-icon">
							<b-avatar size="3em" :src="channel.info?.avatarUrl||channel.avatar_url" badge-variant=""  badge-offset="-0.4em" :href="channel.status=='enabled'&&channel.info?.url||null" target="_blank">
								<template #badge><b-avatar :src="channel.service_icon_url||null" size="1.3em" class="service_avatar" square :title="channel.service_name"></b-avatar></template>
							</b-avatar>
						</div>
						<div class="info">
							<div class="title-row">
								<div class="channel-name">
									<a :href="channel.status=='enabled'&&channel.info?.url||null" target="_blank">{{channel.title}}</a>
									<span v-b-tooltip.hover.bottom v-if="channel?.verified != 1 && channel.status == 'enabled'" title="not verified (bot cannot send messages)"><b-icon icon="x" variant="danger"></b-icon></span>
								</div>
								<div class="channel-buttons">
									<b-button v-if="!compact" size="sm" variant="default" @click="$emit('edit', channel)" title="Edit channel" v-b-tooltip.hover.left><b-icon icon="pencil-fill" style="width: .8em; height: .8em"></b-icon></b-button>
									<b-form-checkbox switch v-model="channel.status" value="enabled" unchecked-value="disabled" @change="setChannelStatus(channel.service_id, channel.token, channel.status, $event)" :disabled="channel.changingStatus" style="margin-right: -0.25rem" :id="`channel-status-${channel.service_id}-${channel.token}`"></b-form-checkbox>
								</div>
							</div>
							<div class="status-row">
								<div class="service-name">
									<template v-if="channel.stream_post?.status === 'pending'">
										<b-spinner small class="mr-1"></b-spinner> Scheduling…
									</template>
									<template v-else-if="channel.stream_post?.status === 'error'">
										<b-icon icon="exclamation-triangle-fill" variant="danger" class="mr-1"></b-icon>
										<span class="text-danger">Schedule failed: {{channel.stream_post.error}}</span>
										<a href="#" class="ml-2 text-muted" @click.prevent="cancelScheduled(channel)">dismiss</a>
									</template>
									<template v-else-if="channel.stream_post?.title">
										<a :href="channel.stream_post.url||null" target="_blank" class="stream-post-link">{{channel.stream_post.title}}</a>
										<a href="#" :class="channel.cancelingSchedule ? 'text-muted' : 'text-danger'" @click.prevent="cancelScheduled(channel)" title="Cancel scheduled post" v-b-tooltip.hover.bottom class="ml-1 flex-shrink-0"><b-icon icon="x-circle-fill"></b-icon></a>
									</template>
									<template v-else>{{channel.service_name}}</template>
								</div>
								<div class="channel-status">
									<b-badge pill :variant="channel.error ? 'danger' : (channel.info?.numLive>0?'success':'secondary')" v-if="channel.status == 'enabled'" :title="channel.info?.numLive>0?'live':'offline'" v-b-tooltip.hover.left class="mr-1 position-relative"><b-spinner v-show="channel.loading > 0" label="Loading" class="mr-2 spinner-border-xs spinner-absolute"></b-spinner> {{channel.info?.numLive>0 ? `${channel.info?.numViewers} viewers` : 'offline'}}</b-badge>
								</div>
							</div>
						</div>
					</div>
					<b-alert variant="danger" :show="channel.channel_status?.authOk === false" class="m-1">Please reauthenticate this channel</b-alert>
				</div>
			</li>
		</draggable>
		<div v-else class="text-center text-muted p-5 flex-fill align-content-end">
			No channels added.<br />
			Please add a channel using the button below.<br />
			<br />
			<br />
			<b-icon icon="arrow-down" font-scale="7.5" animation="cylon-vertical"></b-icon>
		</div>
		<b-popover ref="upgradePopover" :target="upgradePopoverTarget" triggers="manual" placement="right">
			<div class="d-flex justify-content-between align-items-start">
				<h5>Too many channels</h5>

				<b-button
					size="sm"
					variant="link"
					class="p-0 ml-2 text-secondary"
					@click="$refs.upgradePopover.$emit('close')">

					<b-icon icon="x"></b-icon>
				</b-button>
			</div>
			<p>Upgrade your plan to activate more channels.</p>
			<b-button @click="$refs.upgradePopover.$emit('close'); $root.$emit('show-upgrade-modal')">Upgrade now</b-button>
		</b-popover>
	</div>
</template>

<script>
import FetchPoller from '../FetchPoller';
import draggable from 'vuedraggable';

export default {
	components: {
		draggable,
	},
	data() { return {
		loading: 0,
		channels: [],
		user: null,
		showUpgradePopover: false,
		upgradePopoverTarget: '',
	}},
	props: {
		compact: Boolean,
	},
	async mounted() {
		try {
			this.loading++;
			this.error = null;
			this.user = await this.$api.get('userData');
			this.subId = this.$socket.subscribe(this.user.widget_hash, {
				channelInfoUpdate: info => {
					const channel = this.channels.find(c => c.service_id == info.serviceId && c.token == info.channelToken);
					if(!channel) return;
					for(let field of [ 'numViewers', 'numLive', 'title', 'url', 'avatarUrl', 'about', 'createdAt', 'numFollowers', 'numSubscribers' ]) {
						if(!('info' in channel) || !channel.info) channel.info = {};
						if(field in info) this.$set(channel.info, field, info[field]);
					}
				},
				channelDeleted: () => this.channelPoller.start(),
				channelCreated: () => this.channelPoller.start(),
				channelStatusChanged: msg => {
					const channel = this.channels.find(c => c.service_id == msg.serviceId && c.token == msg.channelToken);
					if(!channel) this.channelPoller.start(); // if we can't find the channel, we reload them all
					else {
						channel.status = msg.status;
						if(msg.status == 'disabled')
							channel.poller.stop();
						else if(msg.status == 'enabled')
							channel.poller.start();
					}
				},
			});

			this.channelPoller = new FetchPoller(async signal => {
				try {
					this.error = null;
					const channels = await this.$api.get('channels', null, signal);
					for(let channel of channels) {
						const existingChannel = this.channels.find(c => c.service_id == channel.service_id && c.token == channel.token);
						if(!existingChannel) {
							const newChannel = {
								...channel,
								info: null,
								error: null,
								loading: 0,
								changingStatus: false,
								poller: new FetchPoller(async signal => {
									newChannel.loading++;
									newChannel.error = null;
									try {
										newChannel.info = await this.$api.get(`channel/${encodeURIComponent(channel.service_id)}/${encodeURIComponent(channel.token)}/info`, {}, signal);
									} catch(e) {
										newChannel.error = e;
									} finally {
										newChannel.loading--;
									}
								}, 60 * 1000),
							};
							this.channels.push(newChannel);
							if(channel.status == 'enabled')
								await newChannel.poller.start();
						} else if(existingChannel.status == 'enabled' && channel.status == 'disabled') {
							await existingChannel.poller.stop();
							for(let k in channel)
								existingChannel[k] = channel[k];
						} else if(existingChannel.status == 'disabled' && channel.status == 'enabled') {
							for(let k in channel)
								existingChannel[k] = channel[k];
							await existingChannel.poller.start();
						} else {
							for(let k in channel)
								existingChannel[k] = channel[k];
						}
					}
					for(let i = 0; i < this.channels.length; i++) {
						const existingChannel = channels.find(channel => this.channels[i].service_id == channel.service_id && this.channels[i].token == channel.token);
						if(!existingChannel) {
							await this.channels[i].poller.stop();
							this.channels.splice(i, 1);
							i--;
						} else if(existingChannel.status == 'disabled' && this.channels[i].status == 'enabled') {
							await this.channels[i].poller.stop();
							for(let k in existingChannel)
								this.channels[i][k] = existingChannel[k];
						} else if(existingChannel.status == 'enabled' && this.channels[i].status == 'disabled') {
							for(let k in existingChannel)
								this.channels[i][k] = existingChannel[k];
							await this.channels[i].poller.start();
						} else {
							for(let k in existingChannel)
								this.channels[i][k] = existingChannel[k];
						}
					}
				} catch(e) {
					if(!(e instanceof DOMException) || !(e.code == 20 || e.name == 'AbortError')) {
						this.error = e;
						console.error('Polling channels', e, e instanceof DOMException, e.code, e.name);
					}
				}
			}, 10 * 60 * 1000);
			await this.channelPoller.start(true);
			if(this.channels.length <= 0)
				this.$parent.$refs.channelModal?.show?.();
		} finally {
			this.loading--;
		}
	},
	beforeDestroy() {
		this.subId && this.$socket.unsubscribe(this.subId);
		this.channelPoller.stop();
		for(let i = 0; i < this.channels.length; i++)
			this.channels[i].poller.stop();
	},
	methods: {
		formatScheduled(v) {
			if(!v) return '';
			const d = new Date(v);
			return isNaN(d) ? '' : d.toLocaleString();
		},
		// Optimistically show a pending scheduled post on every enabled channel while the request runs;
		// the next poll replaces these with the real scheduled/error statuses (or clears them).
		markScheduling(scheduledStartTime) {
			for(const ch of this.channels)
				if(ch.status === 'enabled' && ch.channel_status?.authOk !== false && ch.channel_status?.canCreateLivestream)
					this.$set(ch, 'stream_post', { status: 'pending', scheduled_start_at: scheduledStartTime });
		},
		async cancelScheduled(channel) {
			if(channel.cancelingSchedule) return;
			const ok = await this.$bvModal.msgBoxConfirm('Cancel the scheduled post on this channel?', { title: 'Cancel scheduled post', okVariant: 'danger', okTitle: 'Cancel post', cancelTitle: 'Keep it', centered: true });
			if(!ok) return;
			try {
				this.$set(channel, 'cancelingSchedule', true);
				await this.$api.post(`channel/${encodeURIComponent(channel.service_id)}/${encodeURIComponent(channel.token)}/cancelScheduledPost`);
				this.$set(channel, 'stream_post', null);
			} catch(e) {
				console.error('Could not cancel scheduled post', e);
				this.error = e;
			} finally {
				this.$set(channel, 'cancelingSchedule', false);
			}
		},
		async persistOrder() {
			try {
				await this.$api.post('channels/reorder', { ids: this.channels.map(c => c.id).filter(id => id) });
			} catch(e) {
				console.error('Could not save channel order', e);
				this.$bvToast.toast(e.message||e, { title: 'Could not save channel order', variant: 'danger', solid: true });
			}
		},
		async setChannelStatus(serviceId, channelToken, status) {
			if(status == 'enabled')
				this.$refs.upgradePopover.$emit('close');
			this.upgradePopoverTarget = `channel-status-${serviceId}-${channelToken}`;
			try {
				const ch = this.channels.find(c => c.service_id == serviceId && c.token == channelToken);
				if(!ch) throw new Error('Could not find channel in list');
				ch.changingStatus = true;
				await this.$api.post(`channel/${encodeURIComponent(serviceId)}/${encodeURIComponent(channelToken)}/status`, { status });
			} catch(e) {
				console.error('Could not set channel status', serviceId, channelToken, status, e.message, e);
				if(e.message == 'Not subscribed') {
					const ch = this.channels.find(c => c.service_id == serviceId && c.token == channelToken);
					if(ch) {
						ch.status = 'disabled';
						this.$refs.upgradePopover.$emit('open');
					}
				} else {
					this.$bvToast.toast(e.message||e, { title: 'Could not change channel status', variant: 'danger', solid: true });
				}
			} finally {
				const ch = this.channels.find(c => c.service_id == serviceId && c.token == channelToken);
				if(ch) ch.changingStatus = false;
			}
		},
	},
}
</script>

<style lang="scss">
@import '../plugins/variable.scss';

.spinner-border-xs {
	width: .6rem;
	height: .6rem;
	border-radius: .3rem;
	border-width: .05em;
}

.spinner-absolute {
	position: absolute;
	left: -.8rem;
}

.position-relative {
	position: relative;
}

td > .text-right input[type=checkbox] {
	left: inherit;
	right: 1rem;
}

ul.channel-list {
	list-style: none;
	margin: 0;
	border: 0;
	padding: 0;

	> li {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		list-style-type: none;
		padding: 4px;
		transition: background-color .15s;

		&:nth-child(odd) {
			background-color: $table-accent-bg;
		}

		> .channel-drag-handle {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: .7rem;
			margin-right: 2px;
			margin-left: -4px;
			color: $text-muted;
			cursor: grab;
			opacity: 0;
			transition: opacity .15s;
			&:active { cursor: grabbing; }
		}
		&:hover > .channel-drag-handle { opacity: 1; }

		> .channel-rows {
			display: flex;
			flex: 1 1;
			flex-direction: column;
			gap: 4px;
			min-width: 0;

			> .content {
				display: flex;

				> .channel-icon {
					height: 48px;
					margin-right: 12px;
					position: relative;
					transition: all .3s ease;
					width: 48px;

					.b-avatar .b-avatar-badge {
						font-size: 1.2em !important;
						.badge-secondary {
							background-color: transparent !important;
							padding: 0;
						}
					}
				}

				> .info {
					.title-row {
						align-items: center;
						display: flex;
						justify-content: space-between;

						.channel-name {
							align-items: center;
							display: flex;
							transition: opacity .3s ease;
							white-space: nowrap;
							text-overflow: ellipsis;
							overflow: hidden;

							* {
								white-space: nowrap;
								text-overflow: ellipsis;
								overflow: hidden;
							}
						}

						.channel-buttons {
							align-items: center;
							display: flex;
							gap: 8px;
							margin-left: 8px;
						}
					}

					.status-row {
						align-items: center;
						display: flex;
						justify-content: space-between;

						.service-name {
							color: $text-muted;
							font-size: .8em;
							max-width: 18em;
							display: flex;
							align-items: center;
							overflow: hidden;

							.stream-post-link {
								color: inherit;
								overflow: hidden;
								text-overflow: ellipsis;
								white-space: nowrap;
								min-width: 0;
							}
						}
					}

					display: flex;
					flex: 1 1;
					flex-direction: column;
					min-width: 0;
				}
			}
		}
	}
}
</style>
